/**
 * DashboardWrapper is the main wrapper component for all dashboard pages. It is responsible for:
 * - Setting up the main layout of the dashboard (header, sidebar, main content area)
 * - Managing global state related to the dashboard (e.g. modals, mobile helpers, user data)
 * - Initializing third-party libraries (e.g. PubNub for chat, map initialization)
 * - Handling session timeout and displaying the session timeout timer
 * - Preloading common assets (e.g. SVG icons)
 *
 * It uses React Router's Outlet component to render the specific dashboard page content based on the route.
 *
 * Note: This component is wrapped in a ProtectedRoute in the router configuration to ensure that only authenticated users can access the dashboard.
 */

import PubNub from "pubnub";
import { PubNubProvider } from "pubnub-react";
import React, { FC, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router-dom";
import {
  setAccessToken,
  setAccountData,
  setListingQueryParams,
  setMobileHelpersData,
  setModalsData,
  setTimezoneForDateFns,
  setUserData,
  setUserIsImpersonated,
  TAppCommonState,
  TListingQueryParams,
  TMobileHelpers,
  TModalsState,
} from "../../api/store/commonSlice";
import { LegacyOrganizationAccount } from "../../api/types/Account";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../api/types/Onboarding";
import svgIcons from "../../assets/svg/svgIcons";
import { APP_CONFIG } from "../../constants/constants";
import { useMapInit } from "../../hooks/mapInit";
import { useAccountTimezone } from "../../hooks/timezone";
import { ChatContextProvider } from "../../pages/chat/contexts/chat.context";
import Sidemenu from "../sidemenu";
import SysadminAccountUserAccessBar from "../sysadmin/sysadminAccountUserAccessBar";
import DashboardHeader from "./dashboardHeader";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../utils/localStorageItems";
import PageWrapperCommonComps from "../common/PageWrapperCommonComps";

export const dashSidebarModeClasses = {
  expanded: {
    sidebar: "w-64",
    mainAreaOffset: "lg:pl-64",
  },
  collapsed: {
    sidebar: "w-32",
    mainAreaOffset: "lg:pl-32",
  },
};

export const isModalStateActive = (value: unknown): boolean => {
  if (typeof value === "boolean") {
    return value;
  }

  if (value && typeof value === "object") {
    if (
      "show" in value &&
      typeof (value as { show?: unknown }).show === "boolean"
    ) {
      return (value as { show: boolean }).show;
    }

    return true;
  }

  return value !== null && value !== undefined && value !== "";
};

interface DashboardWrapperProps extends React.PropsWithChildren {
  children: React.ReactNode;
}

const DashboardWrapper: FC = () => {
  const { t } = useTranslation();

  // modals state
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const activeModalsState = Object.keys(modalsState).filter((key, index) => {
    if (
      [
        "isExpandedSideMenu", // non-modal state
        "showBuzzerAlertSingle", // excluded, as due to a different logic, this is defaulted to true (unlike other modals)
        "showModalVideoPlayback", // renders inline in the page (not an overlay), so it shouldn't lock page scroll
      ].includes(key)
    ) {
      return false;
    }

    return (
      key.startsWith("show") &&
      isModalStateActive(modalsState[key as keyof TModalsState])
    );
  });
  const modalActive = activeModalsState.length > 0;

  // mobile helpers
  const mobileHelpers = useSelector(
    (state: { commonReducer: { mobileHelpers: TMobileHelpers } }) =>
      state.commonReducer.mobileHelpers
  );

  // update viewportIsMobile flag
  useEffect(() => {
    const handleViewportChanged = () => {
      const isMobile = window.innerWidth < 768;
      if (APP_CONFIG.DEBUG.ALL)
        console.log("=== Viewport changed, isMobile:", isMobile);
      dispatch(
        setMobileHelpersData({
          ...mobileHelpers,
          viewportIsMobile: isMobile,
        })
      );
    };
    handleViewportChanged();
    window.addEventListener("resize", handleViewportChanged);
    return () => {
      window.removeEventListener("resize", handleViewportChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const dispatch = useDispatch();
  const { getLocalStorageItem } = useLocalStorage();

  // setup user data in redux store (if not already set due to page refresh)
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );

  // [re-hydrate redux] re-hydrate user data in redux store on page refresh
  useEffect(() => {
    if (typeof loggedInUserData === "undefined") {
      const ssLoggedInUserData = getLocalStorageItem(
        localStorageKeys.loggedInUserData
      );
      if (!!ssLoggedInUserData) {
        dispatch(setUserData(ssLoggedInUserData));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUserData]);

  // [re-hydrate redux] re-hydrate accessToken in redux store on page refresh
  const accessTokenInStore = useSelector(
    (state: { commonReducer: TAppCommonState }) =>
      state.commonReducer.accessToken
  );
  useEffect(() => {
    if (!accessTokenInStore) {
      const ssAccessToken = getLocalStorageItem(
        localStorageKeys.accessToken
      ) as string | undefined;
      if (ssAccessToken) {
        dispatch(setAccessToken(ssAccessToken));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessTokenInStore]);

  // check localStorage for account data and setup the same in redux store (if not already set due to page refresh)
  // also set timezone for date-fns
  const { convertToTZForDateFns } = useAccountTimezone();
  const accountDataInSS = useMemo(
    () => getLocalStorageItem(localStorageKeys.ownerOrganization) || {},
    [getLocalStorageItem]
  );
  const accountDataInStore = useSelector(
    (state: { commonReducer: { account: LegacyOrganizationAccount } }) =>
      state.commonReducer.account
  );
  useEffect(() => {
    if (!!accountDataInSS && typeof accountDataInStore === "undefined") {
      dispatch(setAccountData(accountDataInSS));
      const accountTimezone =
        accountDataInSS?.timezone || APP_CONFIG.DEFAULT_TIMEZONE;
      const timezoneForDateFns = convertToTZForDateFns(accountTimezone);
      dispatch(setTimezoneForDateFns(timezoneForDateFns));
    }
  }, [accountDataInSS, accountDataInStore, convertToTZForDateFns, dispatch]);

  // check and set impersonated flag if applicable
  const saAccountDataInSS = useMemo(
    () => getLocalStorageItem(`sa_${localStorageKeys.ownerOrganization}`),
    [getLocalStorageItem]
  );
  const userIsImpersonatedInStore = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );
  useEffect(() => {
    if (!!saAccountDataInSS && !userIsImpersonatedInStore) {
      dispatch(setUserIsImpersonated(true));
    }
  }, [saAccountDataInSS, userIsImpersonatedInStore]);

  // [re-hydrate redux] set accountid in listingQueryParams in redux store (if not already set due to page refresh)
  if (!listingQueryParams.users.accountid) {
    const ownerOrganization =
      getLocalStorageItem(localStorageKeys.ownerOrganization) ?? {};
    if (
      !!ownerOrganization &&
      typeof ownerOrganization?.accountid === "string" &&
      ownerOrganization?.accountid?.length > 0
    ) {
      const accountid = ownerOrganization.accountid;
      dispatch(
        setListingQueryParams({
          ...listingQueryParams,
          // ensure the keys below match the keys in listingQueryParams
          users: { ...listingQueryParams.users, accountid },
          roles: { ...listingQueryParams.roles, accountid },
          vehicles: { ...listingQueryParams.vehicles, accountid },
          drivers: { ...listingQueryParams.drivers, accountid },
          groups: { ...listingQueryParams.groups, accountid },
          fleetTags: { ...listingQueryParams.fleetTags, accountid },
          geoZones: { ...listingQueryParams.geoZones, accountid },
          vehicleAlerts: { ...listingQueryParams.vehicleAlerts, accountid },
          vehicleAlertsSingle: {
            ...listingQueryParams.vehicleAlertsSingle,
            accountid,
          },
          buzzerAlerts: { ...listingQueryParams.buzzerAlerts, accountid },
          reports: { ...listingQueryParams.reports, accountid },
          rules: { ...listingQueryParams.rules, accountid },
          maintenance: { ...listingQueryParams.maintenance, accountid },
          documents: { ...listingQueryParams.documents, accountid },
          // accountid not needed for roleCategoryACL
          // accountid not needed for sysadminEnterprises
        })
      );
    }
  }

  // map init moved to useMapInit hook
  const { initMap } = useMapInit();
  initMap();

  // preload svg icons
  const [imagesPreloaded, setImagesPreloaded] = React.useState(true); // as per PM request 10/18/2024, we are not preloading images for quicker? load time
  useEffect(() => {
    const loadImage = (image: string) => {
      return new Promise((resolve, reject) => {
        const loadImg = new Image();
        loadImg.src = image;
        // wait 2 seconds to simulate loading time
        loadImg.onload = () =>
          setTimeout(() => {
            resolve(image);
          }, 2000);

        loadImg.onerror = (err) => reject(err);
      });
    };
    Promise.all(
      Object.keys(svgIcons)
        .map((key) => svgIcons[key as keyof typeof svgIcons])
        .filter((item) => item.indexOf("<svg") === -1)
        .map(loadImage)
    )
      .then(() => setImagesPreloaded(true))
      .catch((err) => console.log("Failed to load images", err));
  }, []);

  // manage primary overlay on login/logout
  useEffect(() => {
    // hide primary overlay on login
    dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: false }));
    // listen to custom event for logout
    const logoutCallback = (e: CustomEvent) => {
      if (APP_CONFIG.DEBUG.ALL) console.log("Logging out...", e?.detail?.cause);
      dispatch(setModalsData({ ...modalsState, showPrimaryOverlay: true }));
    };
    document.addEventListener("logout", (e) =>
      logoutCallback(e as any as CustomEvent)
    );
    return () =>
      document.removeEventListener("logout", (e) =>
        logoutCallback(e as any as CustomEvent)
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userData: LegacyVerifyEmailOtpResponseSuccess = useSelector(
    (state: any) => state.commonReducer.user
  );

  const pubnub = !!userData?.user_profile?.unique_id
    ? new PubNub({
        publishKey: `${process.env.REACT_APP_PUBNUB_PUBLISH_KEY}`,
        subscribeKey: `${process.env.REACT_APP_PUBNUB_SUBSCRIBE_KEY}`,
        userId: userData?.user_profile?.unique_id,
        // authKey: string,
        logVerbosity: APP_CONFIG.DEBUG.PUBNUB_VERBOSE,
        ssl: window.location.protocol === "https:",
        // origin: string,
        // presenceTimeout: number,
        // heartbeatInterval: number,
        // keepAlive: boolean,
        // keepAliveSettings: any,
        // uuid: userData?.user_profile?.unique_id, // Deprecated, use userId instead

        // fileUploadPublishRetryLimit: 0,
        autoNetworkDetection: true, // enable network detection
        restore: true, // restore the subscription on page refresh
      })
    : null;

  const userIsImpersonated = useSelector(
    (state: { commonReducer: { userIsImpersonated: boolean } }) =>
      state.commonReducer.userIsImpersonated
  );

  const hideMobileMenu = () => {
    dispatch(
      setMobileHelpersData({
        ...mobileHelpers,
        showLeftMenuDrawer: false,
      })
    );
  };

  if (!imagesPreloaded)
    return (
      <div
        className={`fixed inset-0 flex items-center justify-center bg-white z-map-loading-overlay`}
      >
        <img
          src={svgIcons.LoadingAnimation}
          alt="Loading animation"
          className={`size-16`}
        />
      </div>
    );

  return (
    <>
      {pubnub === null ? (
        <div className="p-28 text-center">
          <h2 className="font-bold text-2xl text-gray-400">
            {t("fatalErrors.pubnubChatError.heading")}
          </h2>
          <p className="text-gray-400">
            {t("fatalErrors.pubnubChatError.description")}
          </p>
        </div>
      ) : (
        <PubNubProvider client={pubnub}>
          <ChatContextProvider>
            {APP_CONFIG.DEBUG.DISPLAY_VERBOSE && (
              <pre className="no-print fixed top-0 left-0 bg-cyan-700 text-white z-overlay-primary text-xs px-2">
                {`WRAPPER: dashboard`}
              </pre>
            )}
            {/* Dashboard */}
            <section
              className={`container-dashboard bg-white text-base-text ${
                modalActive
                  ? /* added false (below) as this condition doesn't seem appropriate and h-dvh should work better, atleaset on desktop */
                    ` ${userIsImpersonated && false ? "h-[calc(100vh-40px)]" : "h-dvh"} overflow-hidden`
                  : ""
              }${userIsImpersonated ? " pt-10" : ""}`}
            >
              {/* Blue bar - shows when sysadmin user logged in as an account user */}
              {userIsImpersonated && <SysadminAccountUserAccessBar />}

              {/* Mobile Menu */}
              {mobileHelpers.showLeftMenuDrawer && (
                <div
                  className="fixed top-0 right-0 bottom-0 left-0 bg-black/50 backdrop-blur-[4px] z-below-overlay lg:hidden"
                  onClick={hideMobileMenu}
                />
              )}

              {/* Main Dashboard Area */}
              <div
                className={`flex ${userIsImpersonated ? "min-h-[calc(100vh-40px)]" : "min-h-dvh"} grid-cols-12`}
              >
                <aside
                  className={`no-print ${userIsImpersonated ? "h-[calc(100vh-40px)] top-10" : "h-dvh top-0"} ${
                    modalsState.isExpandedSideMenu
                      ? dashSidebarModeClasses.expanded.sidebar
                      : dashSidebarModeClasses.collapsed.sidebar
                  } ${mobileHelpers.showLeftMenuDrawer ? "flex z-overlay" : "hidden"} lg:flex flex-col fixed left-0`}
                >
                  <Sidemenu />
                </aside>
                <div
                  id={`main-dashboard-area`} // used for printing
                  className={`w-full mt-16 lg:mt-0 ${
                    modalsState.isExpandedSideMenu
                      ? dashSidebarModeClasses.expanded.mainAreaOffset
                      : dashSidebarModeClasses.collapsed.mainAreaOffset
                  }`}
                >
                  <header className="no-print">
                    <DashboardHeader />
                  </header>
                  <main>
                    <Outlet />
                  </main>
                </div>
              </div>
            </section>

            {/* Common wrapper components */}
            <PageWrapperCommonComps />
          </ChatContextProvider>
        </PubNubProvider>
      )}
    </>
  );
};

export default DashboardWrapper;
